/**
 * Factory for creating emulator core instances
 * Provides flexibility to switch between different DS emulator implementations
 */

import { IEmulatorCore } from './IEmulatorCore';

export enum EmulatorCoreType {
  MELONDS = 'melonds',
  DESMOND = 'desmond',
}

export interface CoreFactoryConfig {
  preferredCore?: EmulatorCoreType;
  fallbackOrder?: EmulatorCoreType[];
  autoDetectBestCore?: boolean;
}

export class EmulatorCoreFactory {
  private static coreRegistry = new Map<EmulatorCoreType, () => Promise<IEmulatorCore>>();

  /**
   * Register a core constructor
   */
  static registerCore(type: EmulatorCoreType, constructor: () => Promise<IEmulatorCore>): void {
    this.coreRegistry.set(type, constructor);
  }

  /**
   * Create an emulator core instance
   */
  static async createCore(type: EmulatorCoreType): Promise<IEmulatorCore> {
    const constructor = this.coreRegistry.get(type);

    if (!constructor) {
      throw new Error(`Unknown emulator core type: ${type}. Available cores: ${Array.from(this.coreRegistry.keys()).join(', ')}`);
    }

    return constructor();
  }

  /**
   * Create core with fallback logic
   */
  static async createCoreWithFallback(config: CoreFactoryConfig): Promise<IEmulatorCore> {
    const { preferredCore, fallbackOrder, autoDetectBestCore } = config;

    // Auto-detect best core based on browser capabilities
    if (autoDetectBestCore) {
      if (typeof SharedArrayBuffer !== 'undefined') {
        console.log('✓ SharedArrayBuffer available - melonDS can be used');
        // melonDS requires SharedArrayBuffer, but we'll still try Desmond first due to melonDS memory leak
        try {
          return await this.createCore(EmulatorCoreType.DESMOND);
        } catch (error) {
          console.warn('Desmond failed, falling back to melonDS:', error);
          return await this.createCore(EmulatorCoreType.MELONDS);
        }
      } else {
        console.warn('⚠️ SharedArrayBuffer not available - using Desmond only');
        return await this.createCore(EmulatorCoreType.DESMOND);
      }
    }

    // Try preferred core first
    if (preferredCore) {
      try {
        const core = await this.createCore(preferredCore);
        console.log(`✓ Using preferred core: ${preferredCore}`);
        return core;
      } catch (error) {
        console.warn(`Failed to initialize preferred core ${preferredCore}:`, error);
      }
    }

    // Try fallback order
    if (fallbackOrder) {
      for (const coreType of fallbackOrder) {
        try {
          const core = await this.createCore(coreType);
          console.log(`✓ Using fallback core: ${coreType}`);
          return core;
        } catch (error) {
          console.warn(`Failed to initialize fallback core ${coreType}:`, error);
        }
      }
    }

    throw new Error('No emulator core could be initialized');
  }

  /**
   * Get list of available cores
   */
  static getAvailableCores(): EmulatorCoreType[] {
    return Array.from(this.coreRegistry.keys());
  }
}
